import { createInertiaApp } from '@inertiajs/react'
import { StrictMode, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { usePage } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { ThemeProvider } from '../lib/ThemeContext'
import { BreadcrumbProvider } from '../components/navigation/BreadcrumbContext'
import ErrorBoundary from '../components/ErrorBoundary'
import '../lib/i18n'

// Applies the server-supplied locale (from session / user.locale) to i18next
// once per mount. Subsequent client-side changes (LanguageToggle) win until
// the next page navigation provides a fresh `locale` prop.
function LocaleSync({ children }) {
  const { locale } = usePage().props
  const { i18n } = useTranslation()
  const lastSyncedRef = useRef(null)

  useEffect(() => {
    if (!locale) return
    if (lastSyncedRef.current === locale) return
    lastSyncedRef.current = locale
    if (locale !== i18n.language) i18n.changeLanguage(locale)
  }, [locale, i18n])

  return children
}

const WRAPPED_FLAG = Symbol.for('LocaleSync.wrapped')

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.jsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.jsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.jsx'`)
    }

    // Wrap every page in LocaleSync so it can read shared `locale` via
    // usePage() — which only works inside the Inertia component tree.
    // Mutating `page.default.layout` is dangerous: `import.meta.glob` caches
    // module objects, so without this sigil HMR / re-resolves would nest
    // LocaleSync inside itself, and each remount would clobber the user's
    // client-side language choice with the stale server-side `locale` prop.
    if (!page.default[WRAPPED_FLAG]) {
      const existingLayout = page.default.layout
      page.default.layout = (pageNode) => (
        <LocaleSync>
          {existingLayout ? existingLayout(pageNode) : pageNode}
        </LocaleSync>
      )
      page.default[WRAPPED_FLAG] = true
    }

    return page
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <ErrorBoundary>
          <ThemeProvider>
            <BreadcrumbProvider>
              <App {...props} />
            </BreadcrumbProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    )
  }
})
